// Service page content for all 10 services

export const serviceContent = {
  'web-development': {
    title: 'Web Development',
    hero: {
      title: 'Professional Web Development',
      image: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085',
      description: 'Build powerful, scalable websites that drive business growth and deliver exceptional user experiences.'
    },
    overview: {
      title: 'Custom Web Development Solutions',
      content: [
        'In today\'s digital landscape, your website is your business\'s front door. Tecfinity LLC creates custom web solutions that combine stunning design with powerful functionality, ensuring your online presence stands out and converts visitors into customers.',
        'From responsive corporate websites to complex web applications, we leverage cutting-edge technologies to build fast, secure, and scalable platforms. Our development process focuses on user experience, SEO optimization, and seamless integration with your existing systems.'
      ]
    },
    benefits: [
      {
        icon: 'Zap',
        title: 'Lightning-Fast Performance',
        description: 'Optimized code and modern frameworks ensure your website loads in under 2 seconds, improving user engagement and search rankings.'
      },
      {
        icon: 'Shield',
        title: 'Bank-Grade Security',
        description: 'SSL encryption, secure authentication, and regular security audits protect your data and build customer trust.'
      },
      {
        icon: 'TrendingUp',
        title: 'SEO-Optimized Structure',
        description: 'Built-in SEO best practices ensure your website ranks higher in search results and attracts organic traffic.'
      },
      {
        icon: 'Smartphone',
        title: 'Mobile-First Design',
        description: 'Fully responsive layouts that deliver perfect experiences across all devices, from smartphones to desktops.'
      }
    ],
    pricing: [
      {
        name: 'Basic Web Package',
        price: 499,
        description: 'Perfect for startups and small businesses needing a professional online presence.',
        features: [
          '3-5 Page Website',
          'Mobile Responsive Design',
          'Contact Form Integration',
          'Basic SEO Setup',
          'Social Media Integration',
          'FREE Domain & Hosting Setup',
          '48-Hour Initial Delivery',
          'Complete Source Code',
          'Dedicated Project Manager'
        ]
      },
      {
        name: 'Business Web Package',
        price: 899,
        featured: true,
        description: 'Ideal for growing businesses requiring advanced features and functionality.',
        features: [
          '10-15 Custom Pages',
          'Advanced UI/UX Design',
          'CMS Integration',
          'Advanced SEO Optimization',
          'Blog/News Section',
          'Analytics Dashboard',
          'Email Marketing Integration',
          'SSL Certificate',
          'Priority Support',
          'FREE 3 Months Maintenance'
        ]
      },
      {
        name: 'Enterprise Web Package',
        price: 1499,
        description: 'Complete solution for enterprises needing custom web applications.',
        features: [
          'Unlimited Pages',
          'Custom Web Application',
          'Advanced CMS & Admin Panel',
          'API Development & Integration',
          'Multi-language Support',
          'Advanced Security Features',
          'Cloud Hosting Setup',
          'Performance Optimization',
          'Dedicated Development Team',
          'FREE 6 Months Support'
        ]
      }
    ]
  },
  'mobile-app': {
    title: 'Mobile App Development',
    hero: {
      title: 'Native & Cross-Platform Mobile Apps',
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c',
      description: 'Transform your business with powerful mobile applications that engage users and drive growth.'
    },
    overview: {
      title: 'Professional Mobile App Development',
      content: [
        'Mobile apps have become essential for business success. Tecfinity LLC develops intuitive, high-performance mobile applications for iOS and Android that deliver exceptional user experiences and drive customer engagement.',
        'Whether you need a native app for maximum performance or a cross-platform solution for faster deployment, our expert team creates mobile experiences that users love and businesses rely on.'
      ]
    },
    benefits: [
      {
        icon: 'Smartphone',
        title: 'Cross-Platform Excellence',
        description: 'Single codebase for iOS and Android, reducing development time and costs while maintaining native performance.'
      },
      {
        icon: 'Zap',
        title: 'Blazing Performance',
        description: 'Optimized code and efficient architecture ensure smooth, lag-free user experiences.'
      },
      {
        icon: 'Users',
        title: 'Intuitive User Experience',
        description: 'User-centric design that makes navigation effortless and keeps users engaged.'
      },
      {
        icon: 'Shield',
        title: 'Secure & Reliable',
        description: 'Industry-standard security protocols protect user data and ensure app reliability.'
      }
    ],
    pricing: [
      {
        name: 'Basic Mobile App',
        price: 599,
        description: 'Single platform app with essential features for startups.',
        features: [
          'iOS or Android (Choose One)',
          'Up to 10 Screens',
          'User Authentication',
          'Push Notifications',
          'Basic Backend Integration',
          'In-App Analytics',
          'App Store Submission',
          '3 Months Support'
        ]
      },
      {
        name: 'Professional Mobile App',
        price: 999,
        featured: true,
        description: 'Cross-platform app with advanced features for growing businesses.',
        features: [
          'iOS & Android Both',
          'Unlimited Screens',
          'Advanced Authentication',
          'Real-Time Features',
          'Payment Gateway Integration',
          'Advanced Backend & API',
          'Push Notifications & Chat',
          'App Store Optimization',
          '6 Months Priority Support'
        ]
      },
      {
        name: 'Enterprise Mobile App',
        price: 1699,
        description: 'Complete enterprise solution with custom features and integrations.',
        features: [
          'Native iOS & Android',
          'Custom Features & Modules',
          'AI/ML Integration',
          'Advanced Security',
          'Offline Functionality',
          'Third-Party Integrations',
          'Dedicated Development Team',
          'White-Label Solution',
          '1 Year Full Support'
        ]
      }
    ]
  },
  'software-development': {
    title: 'Custom Software Development',
    hero: {
      title: 'Custom Software Development',
      image: 'https://images.unsplash.com/photo-1610563166150-b34df4f3bcd6',
      description: 'Tailored software solutions that solve unique business challenges and drive operational excellence.'
    },
    overview: {
      title: 'Enterprise-Grade Software Solutions',
      content: [
        'No two businesses operate alike, and Tecfinity LLC recognizes this with tailored software solutions designed to solve unique operational challenges. We create scalable, flexible, and secure systems that streamline workflows, improve efficiency, and drive measurable growth.',
        'From CRM platforms to inventory management systems and enterprise-grade applications, we combine technical precision with business insight. Our focus is to reduce manual processes, automate repetitive tasks, and provide software that scales as your needs evolve.'
      ]
    },
    benefits: [
      {
        icon: 'Target',
        title: 'Tailored Fit',
        description: 'Designed specifically for your workflows, our software increases productivity and supports business goals without unnecessary features.'
      },
      {
        icon: 'TrendingUp',
        title: 'Scalable Systems',
        description: 'We build solutions that adapt as you expand, ensuring they meet future challenges without expensive upgrades.'
      },
      {
        icon: 'Shield',
        title: 'Secure Code',
        description: 'Strict coding standards and security checks ensure robust protection against vulnerabilities and unauthorized access.'
      },
      {
        icon: 'Zap',
        title: 'Process Automation',
        description: 'Streamline operations, reduce errors, and save time by automating repetitive tasks across departments.'
      }
    ],
    pricing: [
      {
        name: 'Basic Software Package',
        price: 599,
        description: 'Ideal for small businesses and simple software solutions.',
        features: [
          'Up to 3 Core Features/Modules',
          'Basic User Interface',
          'Single Platform (Web or Desktop)',
          'Standard Functionalities',
          'Limited User Management',
          'Basic Security Features',
          'Dedicated Project Manager',
          'Free Software Logo'
        ]
      },
      {
        name: 'Startup Software Package',
        price: 999,
        featured: true,
        description: 'Suitable for mid-sized businesses with advanced software needs.',
        features: [
          'Up to 7 Advanced Features/Modules',
          'Custom UI/UX Design',
          'Cross-Platform (Web & Desktop)',
          'Secure User Management',
          'Payment Gateway Integration',
          'API Integration',
          'Reporting Dashboard',
          'Cloud Hosting Setup',
          'SSL Security & Encryption'
        ]
      },
      {
        name: 'Professional Software Package',
        price: 1599,
        description: 'Best for enterprises needing fully customized software.',
        features: [
          'Fully Custom & Scalable',
          'Advanced UI/UX',
          'Web, Desktop & Mobile',
          'AI & Automation Integration',
          'Multi-Level User Access',
          'Advanced Security & Compliance',
          'Custom API & Integrations',
          'Dedicated Development Team'
        ]
      }
    ]
  },
  'ecommerce': {
    pricing: [
      {
        name: 'Basic E-Commerce',
        price: 699,
        description: 'Perfect for small online stores starting their journey.',
        features: [
          'Up to 50 Products',
          'Mobile Responsive Design',
          'Secure Payment Gateway',
          'Shopping Cart',
          'Order Management',
          'Basic SEO Setup',
          'SSL Certificate',
          '3 Months Support'
        ]
      },
      {
        name: 'Professional E-Commerce',
        price: 1199,
        featured: true,
        description: 'Complete solution for growing online businesses.',
        features: [
          'Unlimited Products',
          'Advanced Product Filters',
          'Multiple Payment Options',
          'Inventory Management',
          'Customer Reviews',
          'Email Marketing Integration',
          'Analytics Dashboard',
          'Priority Support',
          '6 Months Maintenance'
        ]
      },
      {
        name: 'Enterprise E-Commerce',
        price: 1899,
        description: 'Full-featured platform for large-scale operations.',
        features: [
          'Custom E-Commerce Platform',
          'Multi-Vendor Support',
          'Advanced Analytics',
          'CRM Integration',
          'Multi-Currency & Language',
          'Mobile App Integration',
          'Dedicated Account Manager',
          '1 Year Full Support'
        ]
      }
    ]
  },
  'blockchain': {
    pricing: [
      {
        name: 'Basic Blockchain',
        price: 899,
        description: 'Introduction to blockchain technology for startups.',
        features: [
          'Smart Contract Development',
          'Basic Blockchain Integration',
          'Crypto Wallet Setup',
          'NFT Minting (Basic)',
          'Documentation',
          'Testing & Deployment',
          '3 Months Support'
        ]
      },
      {
        name: 'Professional Blockchain',
        price: 1399,
        featured: true,
        description: 'Advanced blockchain solutions for businesses.',
        features: [
          'Custom Smart Contracts',
          'DApp Development',
          'Token Creation',
          'NFT Marketplace',
          'Web3 Integration',
          'Security Audit',
          'Multi-Chain Support',
          '6 Months Support'
        ]
      },
      {
        name: 'Enterprise Blockchain',
        price: 1999,
        description: 'Complete blockchain ecosystem for enterprises.',
        features: [
          'Private Blockchain Network',
          'Custom Consensus',
          'Enterprise DApp',
          'Advanced Security',
          'Regulatory Compliance',
          'Dedicated Dev Team',
          'Full Infrastructure Setup',
          '1 Year Full Support'
        ]
      }
    ]
  },
  'ui-ux-design': {
    pricing: [
      {
        name: 'Basic UI/UX',
        price: 499,
        description: 'Essential design services for startups.',
        features: [
          'Up to 5 Screens',
          'Wireframe Design',
          'Basic Prototyping',
          'Color Palette',
          'Typography Guide',
          'Design Handoff',
          '2 Revisions'
        ]
      },
      {
        name: 'Professional UI/UX',
        price: 899,
        featured: true,
        description: 'Comprehensive design solution for apps and websites.',
        features: [
          'Up to 15 Screens',
          'High-Fidelity Designs',
          'Interactive Prototype',
          'User Flow Diagrams',
          'Design System',
          'Usability Testing',
          'Developer Handoff',
          '4 Revisions'
        ]
      },
      {
        name: 'Enterprise UI/UX',
        price: 1499,
        description: 'Complete design solution with user research.',
        features: [
          'Unlimited Screens',
          'User Research',
          'Advanced Prototyping',
          'A/B Testing',
          'Complete Design System',
          'Accessibility Compliance',
          'Ongoing Design Support',
          'Unlimited Revisions'
        ]
      }
    ]
  },
  '3d-animation': {
    pricing: [
      {
        name: 'Basic 3D Animation',
        price: 799,
        description: 'Short 3D animations for marketing and products.',
        features: [
          'Up to 30 Seconds',
          'Basic 3D Modeling',
          'Simple Animation',
          'HD Rendering',
          'Background Music',
          '2 Revisions',
          'MP4 Delivery'
        ]
      },
      {
        name: 'Professional 3D Animation',
        price: 1299,
        featured: true,
        description: 'High-quality 3D animations for campaigns.',
        features: [
          'Up to 60 Seconds',
          'Advanced 3D Modeling',
          'Complex Animations',
          '4K Rendering',
          'Custom Sound Design',
          'Visual Effects',
          '4 Revisions',
          'Multiple Formats'
        ]
      },
      {
        name: 'Enterprise 3D Animation',
        price: 1999,
        description: 'Cinematic-quality animations for brands.',
        features: [
          'Up to 120 Seconds',
          'Photorealistic Models',
          'Character Animation',
          'Motion Capture',
          'Advanced VFX',
          'Professional Voice Over',
          'Unlimited Revisions',
          'Source Files Included'
        ]
      }
    ]
  },
  'cloud-solutions': {
    pricing: [
      {
        name: 'Basic Cloud Hosting',
        price: 399,
        description: 'Reliable cloud hosting for small businesses.',
        features: [
          'Shared Cloud Hosting',
          '10GB Storage',
          '100GB Bandwidth',
          'SSL Certificate',
          'Daily Backups',
          'Email Support',
          '99.5% Uptime'
        ]
      },
      {
        name: 'Professional Cloud',
        price: 799,
        featured: true,
        description: 'Scalable cloud infrastructure for growing businesses.',
        features: [
          'Dedicated Cloud Server',
          '50GB Storage',
          'Unlimited Bandwidth',
          'Auto Scaling',
          'Load Balancing',
          'CDN Integration',
          '24/7 Support',
          '99.9% Uptime'
        ]
      },
      {
        name: 'Enterprise Cloud',
        price: 1499,
        description: 'Enterprise-grade cloud infrastructure.',
        features: [
          'Multi-Server Cluster',
          '500GB Storage',
          'Global CDN',
          'Advanced Security',
          'Disaster Recovery',
          'Kubernetes Setup',
          'Dedicated Support Team',
          '99.99% Uptime'
        ]
      }
    ]
  },
  'marketing': {
    pricing: [
      {
        name: 'Basic Marketing',
        price: 599,
        description: 'Essential digital marketing for startups.',
        features: [
          'SEO Optimization',
          'Social Media Setup',
          'Google My Business',
          'Basic Content Creation',
          'Monthly Analytics Report',
          '3 Months Campaign'
        ]
      },
      {
        name: 'Professional Marketing',
        price: 999,
        featured: true,
        description: 'Complete marketing solution for businesses.',
        features: [
          'Advanced SEO',
          'Social Media Management',
          'PPC Campaign Management',
          'Email Marketing',
          'Content Marketing',
          'Lead Generation',
          'Weekly Reports',
          '6 Months Campaign'
        ]
      },
      {
        name: 'Enterprise Marketing',
        price: 1699,
        description: 'Full-scale marketing with dedicated team.',
        features: [
          'Multi-Channel Marketing',
          'Influencer Outreach',
          'Video Marketing',
          'Marketing Automation',
          'CRM Integration',
          'A/B Testing',
          'Dedicated Marketing Team',
          '12 Months Campaign'
        ]
      }
    ]
  },
  'copywriting': {
    pricing: [
      {
        name: 'Basic Copywriting',
        price: 299,
        description: 'Essential copy for websites and landing pages.',
        features: [
          'Up to 1000 Words',
          'Website Copy',
          'SEO Optimization',
          'Basic Editing',
          '2 Revisions',
          '48-Hour Delivery'
        ]
      },
      {
        name: 'Professional Copywriting',
        price: 599,
        featured: true,
        description: 'Complete copywriting for marketing campaigns.',
        features: [
          'Up to 3000 Words',
          'Website + Blog Copy',
          'Ad Copy',
          'Email Sequences',
          'Advanced SEO',
          'Competitor Research',
          '4 Revisions',
          '72-Hour Delivery'
        ]
      },
      {
        name: 'Enterprise Copywriting',
        price: 999,
        description: 'Full content strategy and copywriting.',
        features: [
          'Unlimited Words',
          'Complete Content Strategy',
          'Brand Voice Development',
          'Multi-Channel Copy',
          'Conversion Optimization',
          'A/B Testing Copy',
          'Unlimited Revisions',
          'Priority Delivery'
        ]
      }
    ]
  }
};